import CircleIcon from '@mui/icons-material/Circle';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export type StatusType =
  | 'ACTIVE'
  | 'ARCHIVED'
  | 'INACTIVE'
  | 'SUSPENDED'
  | 'COMPLETED'
  | 'INPROGRESS'
  | 'FAILED'
  | 'UNAVAILABLE'
  | 'ENABLED'
  | 'DISABLED'
  | 'DRAFT'
  | 'TERMINATED'
  | 'FINISHED'
  | 'ERROR'
  | 'PAUSED'
  | 'PUBLISHED'
  | 'UNKNOWN'
  | 'DEPROVISIONED'
  | 'PARTIAL_SUCCESS'
  | 'SUCCESS'
  | 'LOCKED'
  | 'PROCESSING'
  | 'SKIPPED'
  | 'STARTED'
  | 'ACCEPTED'
  | 'PENDING'
  | 'REJECTED'
  | 'EXPIRED'
  | 'UNSPECIFIED'
  | 'IDLE'
  | 'RUNNING';

export const palette = {
  success: {
    lightMode: '#217A49',
    darkMode: '#55C285',
  },
  error: {
    lightMode: '#C42828',
    darkMode: '#F27070',
  },
  warning: {
    lightMode: '#C48A0C',
    darkMode: '#F5CB47',
  },
  info: {
    lightMode: '#1049A8',
    darkMode: '#5699EE',
  },
};

type StatusConfig = {
  label: string;
  variant: keyof typeof palette;
};

interface StatusLabelProps {
  status: StatusType;
  size?: 'small' | 'medium';
  variant?: keyof typeof palette;
}

const STATUS_CONFIG: Record<StatusType, StatusConfig> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  ENABLED: { label: 'Enabled', variant: 'success' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  FINISHED: { label: 'Finished', variant: 'success' },
  PUBLISHED: { label: 'Published', variant: 'success' },
  SUCCESS: { label: 'Success', variant: 'success' },
  ACCEPTED: { label: 'Accepted', variant: 'success' },

  SUSPENDED: { label: 'Suspended', variant: 'warning' },
  PAUSED: { label: 'Paused', variant: 'warning' },
  PARTIAL_SUCCESS: { label: 'Partial Success', variant: 'warning' },
  LOCKED: { label: 'Locked', variant: 'warning' },
  INPROGRESS: { label: 'In Progress', variant: 'warning' },
  PENDING: { label: 'Pending', variant: 'warning' },

  ARCHIVED: { label: 'Archived', variant: 'info' },
  UNAVAILABLE: { label: 'Unavailable', variant: 'info' },
  DRAFT: { label: 'Draft', variant: 'info' },
  PROCESSING: { label: 'Processing', variant: 'info' },
  UNKNOWN: { label: 'Unknown', variant: 'info' },
  DEPROVISIONED: { label: 'Deprovisioned', variant: 'info' },
  SKIPPED: { label: 'Skipped', variant: 'info' },
  STARTED: { label: 'Started', variant: 'info' },
  UNSPECIFIED: {
    label: 'Unspecified',
    variant: 'info',
  },
  IDLE: { label: 'Idle', variant: 'info' },
  RUNNING: { label: 'Running', variant: 'info' },

  INACTIVE: { label: 'Inactive', variant: 'error' },
  FAILED: { label: 'Failed', variant: 'error' },
  TERMINATED: { label: 'Terminated', variant: 'error' },
  ERROR: { label: 'Error', variant: 'error' },
  DISABLED: { label: 'Disabled', variant: 'error' },
  EXPIRED: { label: 'Expired', variant: 'error' },
  REJECTED: { label: 'Rejected', variant: 'error' },
};

export default function StatusLabel({
  status,
  size = 'small',
  variant = 'info',
}: StatusLabelProps) {
  const theme = useTheme();

  const config = STATUS_CONFIG[status as StatusType] ?? {
    label: status,
    variant: variant ?? 'info',
  };

  const color =
    theme.palette.mode === 'light'
      ? palette[config.variant].lightMode
      : palette[config.variant].darkMode;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} height="100%">
      <CircleIcon
        sx={{
          color: color,
          fontSize: size === 'small' ? 12 : 16,
          display: 'block',
          transform: 'translateY(-1px)',
        }}
      />
      <Typography
        sx={{
          color: color,
          fontWeight: 500,
          fontSize: size === 'small' ? 14 : 18,
          lineHeight: 1,
        }}
      >
        {config.label}
      </Typography>
    </Stack>
  );
}
